import React, { useMemo, useState } from 'react';
import OAEGraph from './OAEGraph';
import {
  TEST_TYPES, PURPOSES, ENV_OPTIONS, OAE_PASS_SNR_DB,
  freqsFor, pointAt, overallResult, bilateralSummary,
  normaliseOAE, setEarPoint, clearEar, fmtFreq,
} from './oaeLogic';

/**
 * OAEDataEntry — full data-entry surface for TEOAE / DPOAE.
 *
 * Layout:
 *   • Test-type + purpose selectors (top)
 *   • Two side-by-side ear cards (Right / Left), each with a graph
 *     (click-to-plot) + editable table with auto SNR + auto Pass/Refer
 *   • Bilateral summary badge
 *   • Additional parameter fields (probe / stimulus / equipment /
 *     environment / comments / interpretation / recommendations)
 */
const EarCard = ({ ear, state, freqs, onChangeWhole, plotKind, setPlotKind }) => {
  const earData = state[ear];
  const color = ear === 'right' ? 'red' : 'blue';
  const label = ear === 'right' ? 'Right' : 'Left';
  const overall = overallResult(earData, freqs);

  const setPoint = (freqHz, kind, value) => {
    if (freqHz === '__mode__') { setPlotKind(kind); return; }
    const num = value === '' || value === null ? null : Number(value);
    if (num === null || Number.isFinite(num)) {
      onChangeWhole(setEarPoint(state, ear, freqHz, kind, num));
    }
  };

  return (
    <div
      className={`flex-1 border-2 rounded-md p-2 bg-white ${color === 'red' ? 'border-red-200' : 'border-blue-200'}`}
      data-testid={`oae-ear-${ear}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <h3 className={`text-sm font-bold ${color === 'red' ? 'text-red-700' : 'text-blue-700'}`}>
          {label} Ear
        </h3>
        <button
          type="button"
          data-testid={`oae-clear-${ear}`}
          onClick={() => onChangeWhole(clearEar(state, ear))}
          className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold"
        >Clear</button>
      </div>

      <OAEGraph
        ear={ear}
        earData={earData}
        freqs={freqs}
        plotKind={plotKind}
        onPlotPoint={setPoint}
        passResult={overall}
      />

      <table className="w-full text-[11px] mt-2 border border-slate-300" data-testid={`oae-table-${ear}`}>
        <thead className="bg-slate-100">
          <tr>
            <th className="border border-slate-300 px-1 py-0.5 text-left">Freq (kHz)</th>
            <th className="border border-slate-300 px-1 py-0.5 text-center">OAE (dB SPL)</th>
            <th className="border border-slate-300 px-1 py-0.5 text-center">Noise (dB SPL)</th>
            <th className="border border-slate-300 px-1 py-0.5 text-center">SNR</th>
            <th className="border border-slate-300 px-1 py-0.5 text-center">Result</th>
          </tr>
        </thead>
        <tbody>
          {freqs.map((f) => {
            const { oae, noise, snr, result } = pointAt(earData, f);
            const passColor = result === 'Pass' ? 'text-emerald-700 font-bold' : result === 'Refer' ? 'text-red-700 font-bold' : 'text-slate-400';
            return (
              <tr key={f}>
                <td className="border border-slate-300 px-1 py-0.5 font-mono">{fmtFreq(f)}</td>
                <td className="border border-slate-300 p-0">
                  <input
                    type="number" step="0.1"
                    value={oae === null ? '' : oae}
                    onChange={(e) => setPoint(f, 'oae', e.target.value)}
                    className="w-full px-1 py-0.5 text-center bg-transparent focus:outline-none focus:bg-yellow-50"
                    data-testid={`oae-input-${ear}-${f}-oae`}
                    placeholder="—"
                  />
                </td>
                <td className="border border-slate-300 p-0">
                  <input
                    type="number" step="0.1"
                    value={noise === null ? '' : noise}
                    onChange={(e) => setPoint(f, 'noise', e.target.value)}
                    className="w-full px-1 py-0.5 text-center bg-transparent focus:outline-none focus:bg-yellow-50"
                    data-testid={`oae-input-${ear}-${f}-noise`}
                    placeholder="—"
                  />
                </td>
                <td className="border border-slate-300 px-1 py-0.5 text-center font-mono" data-testid={`oae-snr-${ear}-${f}`}>
                  {snr === null ? '—' : snr.toFixed(1)}
                </td>
                <td className={`border border-slate-300 px-1 py-0.5 text-center ${passColor}`} data-testid={`oae-result-${ear}-${f}`}>
                  {result || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="text-[10px] text-slate-500 mt-0.5 italic">
        SNR = OAE − Noise · Pass when SNR ≥ {OAE_PASS_SNR_DB} dB
      </div>
    </div>
  );
};

const OAEDataEntry = ({ data, onChange }) => {
  const state = useMemo(() => normaliseOAE(data), [data]);
  const [plotKindR, setPlotKindR] = useState('oae');
  const [plotKindL, setPlotKindL] = useState('oae');

  const setField = (key, value) => onChange({ ...state, [key]: value });
  const patchWhole = (next) => onChange(next);

  const freqs = freqsFor(state.test_type);
  const rightResult = overallResult(state.right, freqs);
  const leftResult  = overallResult(state.left, freqs);
  const bilateral   = bilateralSummary(rightResult, leftResult);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-auto p-3" data-testid="oae-data-entry">
      <div className="flex items-center gap-4 mb-3 bg-white border border-slate-200 rounded-md px-3 py-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-700 uppercase">Test:</span>
          {TEST_TYPES.map((t) => (
            <button
              key={t} type="button"
              data-testid={`oae-test-type-${t}`}
              onClick={() => setField('test_type', t)}
              className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${state.test_type === t ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >{t}</button>
          ))}
        </div>
        <div className="h-6 w-px bg-slate-300" />
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-700 uppercase">Purpose:</span>
          {PURPOSES.map((p) => (
            <button
              key={p} type="button"
              data-testid={`oae-purpose-${p}`}
              onClick={() => setField('purpose', p)}
              className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${state.purpose === p ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >{p}</button>
          ))}
        </div>
        {bilateral && (
          <div
            data-testid="oae-bilateral-badge"
            className={`ml-auto px-3 py-1 text-xs font-bold uppercase rounded ${
              bilateral === 'Bilateral Pass' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                : bilateral === 'Bilateral Refer' ? 'bg-red-100 text-red-800 border border-red-300'
                : 'bg-amber-100 text-amber-800 border border-amber-300'
            }`}
          >{bilateral}</div>
        )}
      </div>

      <div className="flex gap-3 mb-3">
        <EarCard ear="right" state={state} freqs={freqs} onChangeWhole={patchWhole} plotKind={plotKindR} setPlotKind={setPlotKindR} />
        <EarCard ear="left"  state={state} freqs={freqs} onChangeWhole={patchWhole} plotKind={plotKindL} setPlotKind={setPlotKindL} />
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3 bg-white border border-slate-200 rounded-md p-2">
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Probe Type</span>
          <input type="text" value={state.probe_type} onChange={(e) => setField('probe_type', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="e.g. Insert probe with tip" data-testid="oae-probe-type" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Stimulus</span>
          <input type="text" value={state.stimulus} onChange={(e) => setField('stimulus', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="e.g. 65/55 dB SPL (L1/L2)" data-testid="oae-stimulus" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Equipment / Tester</span>
          <input type="text" value={state.equipment} onChange={(e) => setField('equipment', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="e.g. GSI Corti / Dr. Rao" data-testid="oae-equipment" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Environment</span>
          <select value={state.environment} onChange={(e) => setField('environment', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-indigo-500"
            data-testid="oae-environment">
            {ENV_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Test Comments</span>
          <textarea value={state.comments} onChange={(e) => setField('comments', e.target.value)} rows={3}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs resize-y focus:outline-none focus:border-indigo-500"
            placeholder="Observations during testing" data-testid="oae-comments" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Interpretation (editable)</span>
          <textarea value={state.interpretation} onChange={(e) => setField('interpretation', e.target.value)} rows={3}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs resize-y focus:outline-none focus:border-indigo-500"
            data-testid="oae-interpretation" />
        </label>
      </div>

      <label className="text-xs mb-3">
        <span className="block text-slate-600 font-semibold mb-0.5">Recommendations</span>
        <textarea value={state.recommendations} onChange={(e) => setField('recommendations', e.target.value)} rows={2}
          className="w-full px-2 py-1 border border-slate-300 rounded text-xs resize-y focus:outline-none focus:border-indigo-500"
          placeholder="Follow-up plan, referrals, hearing-aid recommendations" data-testid="oae-recommendations" />
      </label>
    </div>
  );
};

export default OAEDataEntry;
